import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { AppConfigService, Page } from '@markant-platform/shared/data-access';
import {
  ConsentAndChangeNoticesResponseDto,
  ConsentType,
  LegalDocumentRequestDto,
  LegalDocumentsMetaData,
  UserConsentResponseDto,
  VersionedLegalDocumentDto,
} from './model/consent.models';

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  constructor(private readonly http: HttpClient) {}

  private readonly consentServiceApiUrl: string = AppConfigService.appConfiguration.apiUrl['consentService'];
  private readonly legalDocumentServiceApiUrl: string =
    AppConfigService.appConfiguration.apiUrl['legalDocumentService'];

  getConsentForUserInOrganization(userId: string, organizationId: string): Observable<UserConsentResponseDto[]> {
    return this.http.get<UserConsentResponseDto[]>(`${this.consentServiceApiUrl}/${userId}/${organizationId}`);
  }

  getConsentAndChangeNotices(): Observable<ConsentAndChangeNoticesResponseDto> {
    return this.http.get<ConsentAndChangeNoticesResponseDto>(`${this.consentServiceApiUrl}`);
  }

  deleteInsignificantChangeNotice(id: string): Observable<boolean> {
    return this.http.patch<boolean>(`${this.consentServiceApiUrl}/insignificant-change-notice/${id}`, {});
  }

  getLegalDocument(consentType: ConsentType, dateTime?: string): Observable<VersionedLegalDocumentDto> {
    const query = dateTime ? '?datetime=' + dateTime : '';
    return this.http.get<VersionedLegalDocumentDto>(`${this.legalDocumentServiceApiUrl}/${consentType}${query}`);
  }

  getAllServiceDocumentTypes(page: {
    index: number;
    size: 5 | 10 | 25 | 50 | 100;
  }): Observable<Page<LegalDocumentsMetaData>> {
    return this.http.get<Page<LegalDocumentsMetaData>>(
      `${this.legalDocumentServiceApiUrl}/service-conditions?index=${page.index}&size=${page.size}`
    );
  }

  /**
   * Receives an octet stream and returns an object url to the pdf.
   *
   * @param serviceId - cartridge id to get the legal document for
   * @param targetGroup - organization type to determine legal notice doc (admin only)
   * @param version - version of the legal document
   * @returns {Observable<string>} - object url to the pdf
   */
  downloadServiceDocumentAsObjectUrlByTargetGroupAndVersion(
    serviceId: string,
    targetGroup: string,
    version?: number
  ): Observable<string> {
    let params;

    if (version) {
      params = new HttpParams().set('version', version);
    }

    return this.http
      .get(`${this.legalDocumentServiceApiUrl}/${serviceId}/legal/${targetGroup}`, {
        headers: new HttpHeaders({ Accept: 'application/octet-stream' }),
        responseType: 'blob',
        params: params,
      })
      .pipe(
        map((octetStreamBlob: Blob) =>
          // recreate blob to set type to application/pdf to let the browser know that it is a pdf
          URL.createObjectURL(new Blob([octetStreamBlob], { type: 'application/pdf' }))
        )
      );
  }

  /**
   * Receives an octet stream and returns an object url to the pdf.
   *
   * @param serviceId - cartridge id to get the legal document for
   * @param organizationId - organizationId to determine legal notice doc, if the id is not set the id from the token is used from the endpoint
   * @returns {Observable<string>} - object url to the pdf
   */
  downloadServiceDocumentAsObjectUrl(serviceId: string, organizationId?: string): Observable<string> {
    let params;

    if (organizationId) {
      params = new HttpParams().set('organization-id', organizationId);
    }

    return this.http
      .get(`${this.legalDocumentServiceApiUrl}/${serviceId}/legal`, {
        headers: new HttpHeaders({ Accept: 'application/octet-stream' }),
        responseType: 'blob',
        params: params,
      })
      .pipe(
        map((octetStreamBlob: Blob) =>
          // recreate blob to set type to application/pdf to let the browser know that it is a pdf
          URL.createObjectURL(new Blob([octetStreamBlob], { type: 'application/pdf' }))
        )
      );
  }

  updateServiceDocument(legalDocumentRequestDto: LegalDocumentRequestDto): Observable<string> {
    const payload = new FormData();
    payload.set('file', legalDocumentRequestDto.file, legalDocumentRequestDto.file.name);
    payload.set('typeOfLegalDocumentChange', legalDocumentRequestDto.typeOfLegalDocumentChange);
    payload.set('targetGroup', legalDocumentRequestDto.targetGroup);
    payload.set('changeDescriptionDeDe', legalDocumentRequestDto.changeDescriptionDeDe);
    payload.set('changeDescriptionEnGb', legalDocumentRequestDto.changeDescriptionEnGb);

    if (legalDocumentRequestDto.validFrom) {
      payload.set('validFrom', legalDocumentRequestDto.validFrom);
    }

    return this.http.post(`${this.legalDocumentServiceApiUrl}/${legalDocumentRequestDto.serviceId}/legal`, payload, {
      responseType: 'text',
    });
  }

  approveConsentWithOtp(ticketParam: string): Observable<UserConsentResponseDto[]> {
    return this.http.post<UserConsentResponseDto[]>(`${this.consentServiceApiUrl}/public?ticket=${ticketParam}`, {});
  }

  approveConsent(userId: string, organizationId: string): Observable<UserConsentResponseDto[]> {
    return this.http.post<UserConsentResponseDto[]>(`${this.consentServiceApiUrl}/${userId}/${organizationId}`, {});
  }

  getConsentForOtp(ticketParam: string): Observable<UserConsentResponseDto[]> {
    return this.http.get<UserConsentResponseDto[]>(`${this.consentServiceApiUrl}/public?ticket=${ticketParam}`);
  }
}
